import type { JsonObject } from '@backstage/types';
import type { IconElement } from '@backstage/frontend-plugin-api';
import { CatalogLandingPageIcon } from '../catalog-landing-page';
import { TechDocsLandingPageIcon } from '../techdocs-landing-page';
import {
  RiFocus2Line,
  RiAsterisk,
  RiQuestionAnswerFill,
} from '@remixicon/react';

/**
 * Result type identifiers used by the search backend collators.
 * These values double as the URL path slug under /search.
 * They must match what the collators index as `type`.
 */
export const SEARCH_RESULT_TYPE = {
  SOFTWARE_CATALOG: 'software-catalog',
  TECHDOCS: 'techdocs',
  TECH_RADAR: 'tech-radar',
  QETA: 'qeta',
} as const;

export type SearchResultType =
  (typeof SEARCH_RESULT_TYPE)[keyof typeof SEARCH_RESULT_TYPE];

/**
 * Definition of a search tab (sub-page).
 *
 * - `path`: URL slug under /search and the result type to filter on.
 *   For the "all" tab this is just a slug with no type filtering.
 * - `title`: Display label shown in the tab bar.
 * - `defaultFilters`: Pre-applied search filters when the tab loads.
 */
type SearchTabDefinition = {
  path: string;
  title: string;
  defaultFilters?: JsonObject;
  icon?: IconElement;
};

/** The special "all" path that shows unfiltered results. */
export const ALL_TAB_PATH = 'all';

export const SEARCH_TABS: SearchTabDefinition[] = [
  {
    path: ALL_TAB_PATH,
    title: 'All Sources',
    icon: <RiAsterisk />,
  },
  {
    path: SEARCH_RESULT_TYPE.SOFTWARE_CATALOG,
    title: 'Software Catalog',
    icon: <CatalogLandingPageIcon />,
    defaultFilters: { kind: ['API', 'Component', 'System', 'Template'] },
  },
  {
    path: SEARCH_RESULT_TYPE.TECHDOCS,
    icon: <TechDocsLandingPageIcon />,
    title: 'TechDocs',
  },
  {
    path: SEARCH_RESULT_TYPE.QETA,
    title: 'BackOverflow',
    icon: <RiQuestionAnswerFill />,
  },
  {
    path: SEARCH_RESULT_TYPE.TECH_RADAR,
    title: 'TechRadar',
    icon: <RiFocus2Line />,
  },
];
